import React, { memo, useState, useCallback, useMemo, useEffect, Suspense, lazy } from "react";
import PropTypes from "prop-types";
import {
  Tabs,
  Tab,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Alert,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import InfoIcon from "@mui/icons-material/Info";
import LinkIcon from "@mui/icons-material/Link";

import SuiTypography from "components/SuiTypography";
import { formatCurrency } from "utils/currency";

const ProyeccionPreview = lazy(() => import("./proyeccionPreview"));
import { useProyeccion } from "./proyeccionContext";
import { useProyeccionUpdates } from "../hooks/useProyeccionUpdates";
import { useCuotasMonitor } from "../hooks/useCuotasMonitor";

import TasaInteresField from "./tasaInteresField";
import ResumenCuotas from "./resumenCuotas";
import ClaseVinculadaChip from "./claseVinculadaChip";
import CuotaExcedidaChip from "./cuotaExcedidaChip";

import ProcesosApi from "api/procesosApi";

const ProyeccionTabs = ({
  activeTab,
  setActiveTab,
  deudasPorClase,
  configClases,
  handleConfigChange,
  fechaInicio,
  tieneProyeccion,
  setTieneProyeccion,
  propuestaId,
  proceso,
  setConfigClases,
  proyeccionData,
  setProyeccionData,
  disponible,
  onCambiosPendientes,
  tipo_propuesta,
}) => {
  const { proyeccionState, actualizarEstadoClase, vincularClases } = useProyeccion();
  const [totalCuotasGlobal, setTotalCuotasGlobal] = useState(0);
  const [numeroCuotasTemp, setNumeroCuotasTemp] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [vinculaciones, setVinculaciones] = useState({});

  // Estados temporales para cambios pendientes
  const [cambiosPendientes, setCambiosPendientes] = useState({
    cuotasBase: {},
    numerosCuotas: {},
  });

  // ✅ FUNCIÓN HELPER PARA OBTENER EL CAMPO DE CAPITAL SEGÚN EL TIPO DE PROPUESTA
  const obtenerCampoCapital = useCallback((tipo_propuesta) => {
    switch (tipo_propuesta) {
      case "solicitud":
        return "capital";
      case "conciliacion":
        return "capital_conciliado";
      case "acuerdo":
        return "capital_acuerdo";
      default:
        return "capital"; // Fallback por defecto
    }
  }, []);

  // ✅ FUNCIÓN HELPER PARA OBTENER EL VALOR DE CAPITAL DE UNA DEUDA SEGÚN EL TIPO DE PROPUESTA
  const obtenerValorCapital = useCallback(
    (deuda, tipo_propuesta) => {
      const campoCapital = obtenerCampoCapital(tipo_propuesta);
      return parseFloat(deuda[campoCapital] || 0);
    },
    [obtenerCampoCapital]
  );

  // Calcular total de cuotas usando los datos de la proyección
  // Mantener todas las funciones existentes exactamente igual
  const getClaseOrder = (className) => {
    const orderMap = {
      PRIMERA: 1,
      SEGUNDA: 2,
      TERCERA: 3,
      CUARTA: 4,
      QUINTA: 5,
    };

    const match = className.match(/(PRIMERA|SEGUNDA|TERCERA|CUARTA|QUINTA)/);
    return match ? orderMap[match[0]] : 999;
  };

  const { actualizarTotalCuotas, actualizarVinculaciones } = useProyeccionUpdates(
    proyeccionData,
    setTotalCuotasGlobal,
    setVinculaciones
  );

  // ✅ FUNCIÓN MODIFICADA PARA USAR EL CAMPO CORRECTO SEGÚN EL TIPO DE PROPUESTA
  const calcularTotalesPorClase = useCallback(
    (clase) => {
      const deudas = deudasPorClase[clase]?.deudas || [];

      const totalPostergado = deudas
        .filter((d) => d.postergado || d.costas)
        .reduce((sum, d) => sum + obtenerValorCapital(d, tipo_propuesta), 0);

      const totalActivo = deudas
        .filter((d) => !d.postergado && !d.costas)
        .reduce((sum, d) => sum + obtenerValorCapital(d, tipo_propuesta), 0);

      return {
        totalPostergado,
        totalActivo,
        totalGeneral: totalPostergado + totalActivo,
      };
    },
    [deudasPorClase, tipo_propuesta, obtenerValorCapital]
  );

  useEffect(() => {
    actualizarTotalCuotas();
    actualizarVinculaciones();
  }, [proyeccionData, actualizarTotalCuotas, actualizarVinculaciones]);

  const clases = Object.keys(deudasPorClase || {}).sort((a, b) => {
    return getClaseOrder(a) - getClaseOrder(b);
  });

  const obtenerUltimaFechaClaseAnterior = useCallback(
    (claseActual) => {
      const indiceActual = clases.indexOf(claseActual);
      if (indiceActual <= 0) return null;

      const claseAnterior = clases[indiceActual - 1];
      return proyeccionState.fechasPorClase[claseAnterior];
    },
    [clases, proyeccionState.fechasPorClase]
  );

  const obtenerNumeracionInicial = useCallback(
    (claseActual) => {
      const indiceActual = clases.indexOf(claseActual);
      if (indiceActual <= 0) return 1;

      const claseAnterior = clases[indiceActual - 1];
      return (proyeccionState.cuotasPorClase[claseAnterior] || 0) + 1;
    },
    [clases, proyeccionState.cuotasPorClase]
  );

  // Nueva función para aplicar todos los cambios pendientes
  const aplicarCambiosPendientes = useCallback(async () => {
    try {
      let configActualizada = { ...configClases };
      let huboUpdatesCuotaBase = false;
      let huboUpdatesNumeroCuotas = false;

      console.log("Aplicando cambios pendientes:", cambiosPendientes);
      console.log("Config actual antes de cambios:", configActualizada);

      // Aplicar cambios de cuota base pendientes
      Object.entries(cambiosPendientes.cuotasBase).forEach(([clase, nuevaCuotaBase]) => {
        if (configActualizada[clase]) {
          console.log(`Aplicando nueva cuota base para ${clase}: ${nuevaCuotaBase}`);
          console.log(
            `Modo de cálculo actual para ${clase}:`,
            configActualizada[clase].modo_calculo
          );

          configActualizada[clase] = {
            ...configActualizada[clase],
            cuotaBase: nuevaCuotaBase,
            cuotaBaseOriginal: configActualizada[clase]?.cuotaBaseOriginal ?? nuevaCuotaBase,
            // IMPORTANTE: Si estaba en POR_CUOTAS, cambiar a CUOTA_FIJA
            modo_calculo:
              configActualizada[clase].modo_calculo === "POR_CUOTAS"
                ? "CUOTA_FIJA"
                : configActualizada[clase].modo_calculo,
            // Si se cambió de POR_CUOTAS a CUOTA_FIJA, limpiar número de cuotas
            numero_cuotas:
              configActualizada[clase].modo_calculo === "POR_CUOTAS"
                ? 0
                : configActualizada[clase].numero_cuotas,
          };

          console.log(
            `Modo de cálculo actualizado para ${clase}:`,
            configActualizada[clase].modo_calculo
          );
          huboUpdatesCuotaBase = true;
        }
      });

      // Aplicar cambios de número de cuotas pendientes
      Object.entries(cambiosPendientes.numerosCuotas).forEach(([clase, numeroValor]) => {
        if (configActualizada[clase]) {
          // ✅ USAR EL TOTAL DE DEUDA CALCULADO CON EL CAMPO CORRECTO
          const totales = calcularTotalesPorClase(clase);
          const totalDeuda = totales.totalActivo; // Solo deudas activas (no postergadas)

          if (totalDeuda > 0 && numeroValor > 0) {
            console.log(`Aplicando nuevo número de cuotas para ${clase}: ${numeroValor}`);
            console.log(`Total deuda activa para ${clase}: ${totalDeuda}`);

            // Obtener la primera cuota basada en clases anteriores
            let primeraCuota = 1;
            for (const claseAnterior of clases) {
              if (getClaseOrder(claseAnterior) < getClaseOrder(clase)) {
                if (proyeccionData?.proyeccion?.[claseAnterior]?.proyeccion?.length > 0) {
                  primeraCuota = proyeccionData.proyeccion[claseAnterior].ultima_cuota + 1;
                }
              }
            }

            configActualizada[clase] = {
              ...configActualizada[clase],
              modo_calculo: "POR_CUOTAS",
              numero_cuotas: numeroValor,
              primera_cuota: primeraCuota,
              cuotaBase: configActualizada[clase]?.cuotaBase || 0,
              tasaBase: configActualizada[clase]?.tasaBase || 0,
              total_deuda: totalDeuda,
            };
            huboUpdatesNumeroCuotas = true;
          }
        }
      });

      // Si hubo cambios, regenerar textos
      if (huboUpdatesCuotaBase || huboUpdatesNumeroCuotas) {
        configActualizada = regenerarTextosConfiguracion(
          configActualizada,
          proyeccionData?.clases_vinculadas || {}
        );
        console.log("Config actualizada después de cambios:", configActualizada);

        // Actualizar el estado de manera síncrona usando una promesa
        await new Promise((resolve) => {
          setConfigClases(configActualizada);
          // Usar setTimeout para asegurar que el estado se actualice
          setTimeout(resolve, 0);
        });
      }

      // Limpiar cambios pendientes después de aplicarlos
      setCambiosPendientes({
        cuotasBase: {},
        numerosCuotas: {},
      });

      console.log("Retornando configuración actualizada:", configActualizada);
      return configActualizada;
    } catch (error) {
      console.error("Error al aplicar cambios pendientes:", error);
      throw error;
    }
  }, [
    cambiosPendientes,
    configClases,
    deudasPorClase,
    clases,
    proyeccionData,
    calcularTotalesPorClase,
  ]);

  // Exponer la función al padre a través del callback
  useEffect(() => {
    if (onCambiosPendientes) {
      onCambiosPendientes(aplicarCambiosPendientes);
    }
  }, [aplicarCambiosPendientes, onCambiosPendientes]);

  const actualizarProyeccionYRefrescar = async ({
    propuestaId,
    setProyeccionData,
    setConfigClases,
    setTieneProyeccion,
    regenerarTextosConfiguracion = (config) => config, // fallback si no hay función
  }) => {
    try {
      const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuestaId);
      if (proyeccionResponse?.data) {
        const nuevaConfig = regenerarTextosConfiguracion(
          proyeccionResponse.data.configuracion,
          proyeccionResponse.data.clases_vinculadas || {}
        );
        setConfigClases(nuevaConfig);
        setProyeccionData(proyeccionResponse.data);
        setTieneProyeccion(false);
        setTimeout(() => setTieneProyeccion(true), 0);
      }
    } catch (error) {
      console.error("Error al actualizar y refrescar proyección:", error);
      throw error;
    }
  };

  const handleVincularClase = useCallback(
    async (clase, claseAVincular) => {
      try {
        // ✅ USAR LOS TOTALES CALCULADOS CON EL CAMPO CORRECTO
        const totalesClase1 = calcularTotalesPorClase(clase);
        const totalesClase2 = calcularTotalesPorClase(claseAVincular);

        // Hacer copia profunda de las configuraciones existentes
        const configClase1 = {
          ...configClases[clase],
          cuota_base: configClases[clase]?.cuota_base || configClases[clase]?.cuotaBase || 0,
          tasa_base: configClases[clase]?.tasa_base || configClases[clase]?.tasaBase || 0,
          total_deuda: configClases[clase]?.total_deuda || totalesClase1.totalActivo || 0,
          modo_calculo: configClases[clase]?.modo_calculo,
          numero_cuotas: configClases[clase]?.numero_cuotas,
          modificaciones: configClases[clase]?.modificaciones || {},
          descripcion: configClases[clase]?.descripcion || "",
          // Preservar campos adicionales si existen
          cuota_calculada: configClases[clase]?.cuota_calculada,
          ultima_cuota: configClases[clase]?.ultima_cuota,
          ultima_fecha: configClases[clase]?.ultima_fecha,
        };
        const configClase2 = {
          ...configClases[claseAVincular],
          cuota_base:
            configClases[claseAVincular]?.cuota_base ||
            configClases[claseAVincular]?.cuotaBase ||
            0,
          tasa_base:
            configClases[claseAVincular]?.tasa_base || configClases[claseAVincular]?.tasaBase || 0,
          total_deuda: configClases[claseAVincular]?.total_deuda || totalesClase2.totalActivo || 0,
          modo_calculo: configClases[claseAVincular]?.modo_calculo,
          numero_cuotas: configClases[claseAVincular]?.numero_cuotas,
          modificaciones: configClases[claseAVincular]?.modificaciones || {},
          descripcion: configClases[claseAVincular]?.descripcion || "",
          // Preservar campos adicionales si existen
          cuota_calculada: configClases[claseAVincular]?.cuota_calculada,
          ultima_cuota: configClases[claseAVincular]?.ultima_cuota,
          ultima_fecha: configClases[claseAVincular]?.ultima_fecha,
        };

        // Establecer modo_calculo por defecto solo si no existe
        if (!configClase1.modo_calculo) configClase1.modo_calculo = "CUOTA_FIJA";
        if (!configClase2.modo_calculo) configClase2.modo_calculo = "CUOTA_FIJA";

        // Mantener la configuración existente
        let nuevaConfiguracion = { ...configClases };

        // Aplicar lógica de cuotas preservando POR_CUOTAS
        if (configClase1.modo_calculo === "POR_CUOTAS") {
          // Mantener configuración POR_CUOTAS de clase1 intacta
          configClase2.modo_calculo = "CUOTA_FIJA";
          if (configClase1.ultima_cuota) {
            configClase2.ultima_cuota = configClase1.ultima_cuota;
            configClase2.ultima_fecha = configClase1.ultima_fecha;
          }
        } else if (configClase2.modo_calculo === "POR_CUOTAS") {
          // Mantener configuración POR_CUOTAS de clase2 intacta
          configClase1.modo_calculo = "CUOTA_FIJA";
          if (configClase2.ultima_cuota) {
            configClase1.ultima_cuota = configClase2.ultima_cuota;
            configClase1.ultima_fecha = configClase2.ultima_fecha;
          }
        }

        // Actualizar solo las clases involucradas
        nuevaConfiguracion[clase] = configClase1;
        nuevaConfiguracion[claseAVincular] = configClase2;

        const vinculacionesAEnviar = {
          [clase]: [claseAVincular],
          [claseAVincular]: [clase],
        };

        const data = {
          proceso: proceso,
          fecha_inicio: fechaInicio.format("YYYY-MM-DD"),
          configuracion: nuevaConfiguracion,
          clases_vinculadas: vinculacionesAEnviar,
        };

        const response = await ProcesosApi.actualizarPropuesta(propuestaId, data);
        if (response?.status === 200) {
          await actualizarProyeccionYRefrescar({
            propuestaId,
            setProyeccionData,
            setConfigClases,
            setTieneProyeccion,
            regenerarTextosConfiguracion,
          });
        }
      } catch (error) {
        console.error("Error al vincular clases:", error);
        throw error;
      }
    },
    [configClases, propuestaId, vincularClases, proceso, fechaInicio, calcularTotalesPorClase]
  );

  const handleDesvincular = useCallback(
    async (clase, claseVinculada) => {
      try {
        // ✅ USAR LOS TOTALES CALCULADOS CON EL CAMPO CORRECTO
        const totalesClase1 = calcularTotalesPorClase(clase);
        const totalesClase2 = calcularTotalesPorClase(claseVinculada);

        // Crear una copia de la configuración actual
        let nuevaConfiguracion = { ...configClases };

        // Resetear la configuración de ambas clases a sus valores base
        const resetearClase = (nombreClase, totales) => ({
          ...configClases[nombreClase],
          cuota_base: configClases[nombreClase]?.cuotaBaseOriginal,
          cuota_calculada: undefined,
          ultima_cuota: 0,
          ultima_fecha: null,
          tasa_base: configClases[nombreClase]?.tasaBase || 0,
          total_deuda: configClases[nombreClase]?.totalDeuda || totales.totalActivo || 0,
          descripcion: configClases[nombreClase]?.descripcion || "",
          modificaciones: {}, // Limpiar modificaciones
          modo_calculo: "CUOTA_FIJA",
        });

        // Aplicar el reset a ambas clases
        nuevaConfiguracion[clase] = resetearClase(clase, totalesClase1);
        nuevaConfiguracion[claseVinculada] = resetearClase(claseVinculada, totalesClase2);

        // Crear nuevas vinculaciones eliminando la relación actual
        const nuevasVinculaciones = {};
        if (proyeccionData?.clases_vinculadas) {
          Object.entries(proyeccionData.clases_vinculadas).forEach(([k, v]) => {
            if (k !== clase && k !== claseVinculada) {
              nuevasVinculaciones[k] = v.filter((c) => c !== clase && c !== claseVinculada);
              if (nuevasVinculaciones[k].length === 0) {
                delete nuevasVinculaciones[k];
              }
            }
          });
        }

        // Preparar los datos para enviar al backend
        const data = {
          proceso: proceso,
          fecha_inicio: fechaInicio.format("YYYY-MM-DD"),
          configuracion: nuevaConfiguracion,
          clases_vinculadas: nuevasVinculaciones,
        };

        // Enviar la actualización al backend
        const response = await ProcesosApi.actualizarPropuesta(propuestaId, data);
        if (response?.status === 200) {
          await actualizarProyeccionYRefrescar({
            propuestaId,
            setProyeccionData,
            setConfigClases,
            setTieneProyeccion,
            regenerarTextosConfiguracion,
          });
        }
      } catch (error) {
        console.error("Error al desvincular clases:", error);
        throw error;
      }
    },
    [configClases, propuestaId, proceso, fechaInicio, proyeccionData, calcularTotalesPorClase]
  );

  const obtenerNumeroCuotas = useCallback(
    (clase) => {
      // Primero revisar si hay cambios pendientes
      if (cambiosPendientes.numerosCuotas[clase] !== undefined) {
        return cambiosPendientes.numerosCuotas[clase];
      }
      // Luego revisar el estado temporal
      if (numeroCuotasTemp[clase] !== undefined) {
        return numeroCuotasTemp[clase];
      }
      // Luego revisar la proyección actual usando la longitud del array de proyección
      if (proyeccionData?.proyeccion?.[clase]?.proyeccion?.length) {
        return proyeccionData.proyeccion[clase].proyeccion.length;
      }
      // Si no hay proyección, revisar la configuración
      if (configClases[clase]?.ultima_cuota !== undefined) {
        return configClases[clase].ultima_cuota;
      }
      return configClases[clase]?.numero_cuotas || 0;
    },
    [cambiosPendientes.numerosCuotas, numeroCuotasTemp, proyeccionData, configClases]
  );

  // Función modificada para solo almacenar cambios temporalmente
  const handleNumeroCuotasChange = (clase, valor) => {
    const numeroValor = parseInt(valor) || 0;
    console.log(`Guardando cambio de número de cuotas para ${clase}: ${numeroValor}`);

    // Solo actualizar el estado temporal y cambios pendientes
    setNumeroCuotasTemp((prev) => ({
      ...prev,
      [clase]: numeroValor,
    }));

    setCambiosPendientes((prev) => {
      const nuevoCambio = {
        ...prev,
        numerosCuotas: {
          ...prev.numerosCuotas,
          [clase]: numeroValor,
        },
      };
      console.log("Nuevos cambios pendientes (cuotas):", nuevoCambio);
      return nuevoCambio;
    });
  };

  const handleModificarCuotaTab = useCallback(
    async (clase, fecha, valor, tasa, numeroCuota, propagarCambios = false) => {
      try {
        // ✅ USAR LOS TOTALES CALCULADOS CON EL CAMPO CORRECTO
        const totalesClase = calcularTotalesPorClase(clase);

        const nuevaConfigClase = {
          ...configClases[clase],
          modificaciones: {
            ...configClases[clase].modificaciones,
            [fecha]: {
              cuota: valor,
              tasa,
              fecha,
              numeroCuota,
              propagarCambios,
            },
          },
        };
        const nuevaConfiguracion = {
          ...configClases,
          [clase]: {
            ...nuevaConfigClase,
            cuota_base: configClases[clase].cuotaBase || configClases[clase].cuota_base,
            tasa_base: configClases[clase].tasaBase || configClases[clase].tasa_base,
            descripcion: configClases[clase].descripcion || "",
            total_deuda:
              configClases[clase].totalDeuda ||
              configClases[clase].total_deuda ||
              totalesClase.totalActivo,
          },
        };
        const data = {
          proceso: proceso,
          fecha_inicio: fechaInicio.format("YYYY-MM-DD"),
          configuracion: nuevaConfiguracion,
          clase_nombre: clase,
          fecha: fecha,
          nueva_cuota: valor,
          nueva_tasa: tasa,
          numero_cuota: numeroCuota,
          propagarCambios,
        };
        // Si alguna clase tenía POR_CUOTAS y se modificó manualmente la cuota
        Object.entries(configClases).forEach(([clase, config]) => {
          if (
            config.modo_calculo === "CUOTA_FIJA" &&
            data.configuracion[clase]?.modo_calculo === "POR_CUOTAS"
          ) {
            data.configuracion[clase] = {
              ...data.configuracion[clase],
              modo_calculo: "CUOTA_FIJA",
              numero_cuotas: 0,
              primera_cuota: undefined,
              cuota_final: undefined,
              cuota_calculada: undefined,
            };
          }
        });
        const response = await ProcesosApi.actualizarPropuesta(propuestaId, data);
        if (response.status === 200) {
          const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuestaId);
          if (proyeccionResponse?.data?.configuracion) {
            setConfigClases(proyeccionResponse.data.configuracion);
          }
          const claseIndex = clases.indexOf(clase);
          const clasesAfectadas = clases.slice(claseIndex + 1);
          for (const claseAfectada of clasesAfectadas) {
            if (proyeccionState.fechasPorClase[clase]) {
              actualizarEstadoClase(claseAfectada, null, proyeccionState.fechasPorClase[clase]);
            }
          }
          return response;
        }
      } catch (error) {
        console.error("Error en handleModificarCuotaTab:", error);
        throw error;
      }
    },
    [
      configClases,
      proceso,
      propuestaId,
      fechaInicio,
      clases,
      actualizarEstadoClase,
      proyeccionState,
      calcularTotalesPorClase,
    ]
  );

  // Función modificada para solo almacenar cambios temporalmente
  const handleCuotaBaseChange = (clase, nuevoValor) => {
    console.log(`Guardando cambio de cuota base para ${clase}: ${nuevoValor}`);
    // Solo actualizar estados temporales y cambios pendientes
    setCambiosPendientes((prev) => {
      const nuevoCambio = {
        ...prev,
        cuotasBase: {
          ...prev.cuotasBase,
          [clase]: nuevoValor,
        },
      };
      console.log("Nuevos cambios pendientes:", nuevoCambio);
      return nuevoCambio;
    });
  };

  const generateConfigText = (
    clase,
    cuotaBase,
    tasaBase,
    descripcionAdicional = "",
    configClases,
    vinculaciones
  ) => {
    const parts = [];
    // Verificar si la clase está vinculada
    let claseVinculada = null;
    if (vinculaciones && Object.keys(vinculaciones).length > 0) {
      if (vinculaciones[clase]?.length) {
        claseVinculada = vinculaciones[clase][0];
      } else {
        for (const [key, vinculadas] of Object.entries(vinculaciones)) {
          if (vinculadas.includes(clase)) {
            claseVinculada = key;
            break;
          }
        }
      }
    }
    const configActual = configClases[clase] || {};
    const modoCalculo = configActual.modo_calculo;
    if (claseVinculada && getClaseOrder(clase) < getClaseOrder(claseVinculada)) {
      const configVinculada = configClases[claseVinculada] || {};
      const cuotaVinculada = configVinculada.cuota_base || configVinculada.cuotaBase || 0;
      parts.push({
        tipo: "VINCULADA",
        clase: clase,
        claseVinculada: claseVinculada,
        valor: [cuotaBase, cuotaVinculada],
        texto: `Ofrezco pagar simultáneamente una cuota mensual por valor de ${
          formatCurrency(cuotaBase).formatted
        } para la ${clase} ${
          modoCalculo === "POR_CUOTAS" ? `a ${configActual.numero_cuotas} cuotas` : ""
        } y ${formatCurrency(cuotaVinculada).formatted} para la ${claseVinculada} ${
          configVinculada.modo_calculo === "POR_CUOTAS"
            ? `a ${configVinculada.numero_cuotas} cuotas`
            : ""
        } que serán canceladas de acuerdo a la prelación de créditos establecida por la ley`,
      });
      parts.push({
        tipo: "INTERES_VINCULADO",
        clase: clase,
        claseVinculada: claseVinculada,
        valor: [tasaBase, configVinculada.tasa_base || configVinculada.tasaBase || 0],
        texto: `Reconozco un ${tasaBase.toFixed(1)}% de interés futuro para la ${clase} y un ${(
          configVinculada.tasa_base ||
          configVinculada.tasaBase ||
          0
        ).toFixed(1)}% de interés futuro para la ${claseVinculada}`,
      });
    } else if (!claseVinculada) {
      if (cuotaBase > 0) {
        parts.push({
          tipo: "CUOTA",
          clase: clase,
          valor: cuotaBase,
          texto: `Ofrezco una cuota mensual por valor de ${
            formatCurrency(cuotaBase).formatted
          } para la ${clase} ${
            modoCalculo === "POR_CUOTAS" ? `a ${configActual.numero_cuotas} cuotas` : ""
          } que será cancelada de acuerdo a la prelación de créditos establecida por la ley`,
        });
      }
      if (tasaBase > 0) {
        parts.push({
          tipo: "INTERES",
          clase: clase,
          valor: tasaBase,
          texto: `Reconozco un ${tasaBase.toFixed(1)}% de interés futuro para la ${clase}`,
        });
      }
    }
    if (descripcionAdicional?.trim()) {
      parts.push({
        tipo: "ADICIONAL",
        clase: clase,
        texto: descripcionAdicional.trim(),
      });
    }
    return parts;
  };

  const regenerarTextosConfiguracion = (configuracion, vinculacionesActuales) => {
    const nuevaConfiguracion = { ...configuracion };
    Object.keys(configuracion).forEach((clase) => {
      nuevaConfiguracion[clase] = {
        ...configuracion[clase],
        textoGenerado: generateConfigText(
          clase,
          configuracion[clase]?.cuotaBase || 0,
          configuracion[clase]?.tasaBase || 0,
          configuracion[clase]?.descripcion || "",
          configuracion,
          vinculacionesActuales
        ),
      };
    });
    return nuevaConfiguracion;
  };

  const renderTabs = useMemo(
    () => (
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.9rem",
            color: "text.secondary",
            px: 1,
            py: 1.5,
            borderRadius: "8px 8px 0 0",
            transition: "all 0.3s ease",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          },
          "& .Mui-selected": {
            color: "primary.contrastText",
            backgroundColor: "primary.main",
            boxShadow: "0 -4px 8px rgba(0,0,0,0.1)",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "primary.main",
              color: "primary.contrastText",
            },
          },
        }}
      >
        {clases.map((clase, index) => {
          const totales = calcularTotalesPorClase(clase);
          const tienePostergados = totales.totalPostergado > 0;
          return (
            <Tab
              key={clase}
              label={
                <Stack sx={{ gap: "2px" }}>
                  <Box>{`${clase} (${formatCurrency(totales.totalActivo).formatted})`}</Box>
                  {tienePostergados && (
                    <Chip
                      size="small"
                      label={`Postergado: ${formatCurrency(totales.totalPostergado).formatted}`}
                      color="warning"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  )}
                </Stack>
              }
              value={index}
            />
          );
        })}
      </Tabs>
    ),
    [activeTab, clases, calcularTotalesPorClase]
  );

  const { excesos, isMonitoring, hayExcesos } = useCuotasMonitor(
    proyeccionData,
    disponible,
    configClases,
    proyeccionData?.clases_vinculadas || {}
  );

  const obtenerCuotaBase = useCallback(
    (clase) => {
      // Primero revisar si hay cambios pendientes
      if (cambiosPendientes.cuotasBase[clase] !== undefined) {
        return cambiosPendientes.cuotasBase[clase];
      }
      // Si hay proyección, usar el valor de la primera cuota
      if (proyeccionData?.proyeccion?.[clase]?.proyeccion?.[0]) {
        return proyeccionData.proyeccion[clase].proyeccion[0].cuota;
      }
      // Si no hay proyección, usar el valor configurado o disponible
      return configClases[clase]?.cuotaBase || configClases[clase]?.cuota_base || disponible;
    },
    [cambiosPendientes.cuotasBase, proyeccionData, configClases, disponible]
  );

  // Agregar este useEffect para mantener todo sincronizado
  useEffect(() => {
    if (tieneProyeccion && proyeccionData?.proyeccion) {
      setConfigClases((prevConfig) => {
        const newConfig = { ...prevConfig };
        Object.entries(proyeccionData.proyeccion).forEach(([clase, data]) => {
          if (data?.proyeccion?.[0]) {
            newConfig[clase] = {
              ...newConfig[clase],
              cuotaBase:
                newConfig[clase]?.cuotaBaseOriginal ??
                data.proyeccion[0]?.cuota ??
                newConfig[clase]?.cuotaBase,
              cuotaBaseOriginal: newConfig[clase]?.cuotaBaseOriginal ?? data.proyeccion[0]?.cuota,
              // Mantener la lógica existente para tasaBase
              tasaBase:
                proyeccionData.configuracion?.[clase]?.tasa_base ||
                prevConfig[clase]?.tasa_base ||
                prevConfig[clase]?.tasaBase ||
                0,
              // Mantener todas las propiedades existentes
              numero_cuotas: data.proyeccion.length,
              ultima_cuota: data.ultima_cuota,
              ultima_fecha: data.fecha_final,
              // Preservar el modo de cálculo si existe
              modo_calculo:
                proyeccionData.configuracion?.[clase]?.modo_calculo ||
                prevConfig[clase]?.modo_calculo ||
                "CUOTA_FIJA",
              // Preservar otras propiedades importantes
              modificaciones: prevConfig[clase]?.modificaciones || {},
              descripcion: prevConfig[clase]?.descripcion || "",
            };
          }
        });
        return newConfig;
      });

      // Limpiar cambios pendientes cuando se carga una nueva proyección
      setCambiosPendientes({
        cuotasBase: {},
        numerosCuotas: {},
      });
    }
  }, [tieneProyeccion, proyeccionData]);

  const [isEditingCuotaBase, setIsEditingCuotaBase] = useState(false);
  const [cuotaBaseValue, setCuotaBaseValue] = useState("");

  console.log("Renderizando ProyeccionTabs con clases:", clases);
  console.log("Tipo de propuesta:", tipo_propuesta);

  return (
    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
      {/* Monitor de Excesos */}
      {isMonitoring && hayExcesos && tipo_propuesta && (
        <Grid item size={12}>
          <Alert
            severity="warning"
            action={
              <Tooltip title="Información sobre excesos en cuotas">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            <Stack sx={{ gap: "2px" }}>
              <SuiTypography variant="subtitle2">
                Se han detectado cuotas que superan el disponible mensual de{" "}
                {formatCurrency(disponible).formatted}:
              </SuiTypography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {Object.entries(excesos)
                  // eslint-disable-next-line no-unused-vars
                  .filter(([_, data]) => data.tipo !== "VINCULADA_REFERENCIA")
                  .map(([clase, data]) => {
                    if (!data.excedentes) return null;
                    return data.excedentes.map((excedente, index) => (
                      <CuotaExcedidaChip
                        key={`${clase}-${index}`}
                        clase={clase}
                        cuotaInicial={excedente.cuotaInicial}
                        cuotaFinal={excedente.cuotaFinal}
                        excedente={excedente.excedente}
                        clasesVinculadas={excedente.clasesVinculadas || []}
                        formatCurrency={formatCurrency}
                      />
                    ));
                  })}
              </Stack>
            </Stack>
          </Alert>
        </Grid>
      )}

      {/* ✅ ALERT INFORMATIVO SOBRE EL TIPO DE PROPUESTA */}
      {/* {tipo_propuesta && (
        <Grid item size={12}>
          <Alert severity="info" sx={{ mb: 1 }}>
            <SuiTypography variant="subtitle2">
              <strong>Tipo de propuesta:</strong> {tipo_propuesta.toUpperCase()}
              {tipo_propuesta === "solicitud" && " - Usando valores de capital original"}
              {tipo_propuesta === "conciliacion" && " - Usando valores de capital conciliado"}
              {tipo_propuesta === "acuerdo" && " - Usando valores de capital acordado"}
            </SuiTypography>
          </Alert>
        </Grid>
      )} */}

      <Grid item size={12}>
        {renderTabs}
        {clases.map((clase, index) => {
          // Calculamos los totales para esta clase
          const totales = calcularTotalesPorClase(clase);
          const tienePostergados = totales.totalPostergado > 0;
          return (
            <Box key={clase} hidden={activeTab !== index}>
              {/* AQUÍ agregamos el alert de deudas postergadas */}
              {tienePostergados && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Stack spacing={1}>
                    <SuiTypography variant="subtitle2">
                      Esta clase contiene deudas postergadas que no serán incluidas en el cálculo de
                      cuotas:
                    </SuiTypography>
                    <Box>
                      <strong>Total Postergado o Costas:</strong>{" "}
                      {formatCurrency(totales.totalPostergado).formatted}
                      <br />
                      <strong>Total Activo:</strong> {formatCurrency(totales.totalActivo).formatted}
                      <br />
                      <strong>Total General:</strong>{" "}
                      {formatCurrency(totales.totalGeneral).formatted}
                    </Box>
                  </Stack>
                </Alert>
              )}
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={12}>
                  <ResumenCuotas
                    totalCuotas={totalCuotasGlobal}
                    clase={clase}
                    proyeccionData={proyeccionData}
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={4}>
                  <SuiTypography variant="body2" fontWeight="bold">
                    Cuota Base
                  </SuiTypography>
                  <TextField
                    value={
                      isEditingCuotaBase
                        ? formatCurrency(parseFloat(cuotaBaseValue) || 0).formatted
                        : formatCurrency(obtenerCuotaBase(clase)).formatted
                    }
                    onChange={(e) => {
                      setIsEditingCuotaBase(true);
                      const rawValue = e.target.value.replace(/[^\d]/g, "");
                      setCuotaBaseValue(rawValue);
                    }}
                    onFocus={() => {
                      setIsEditingCuotaBase(true);
                      setCuotaBaseValue(
                        formatCurrency(obtenerCuotaBase(clase)).formatted.replace(/[^\d]/g, "")
                      );
                    }}
                    onBlur={() => {
                      setIsEditingCuotaBase(false);
                      const valorNumerico = cuotaBaseValue || 0;
                      handleCuotaBaseChange(clase, valorNumerico);
                      setCuotaBaseValue("");
                    }}
                    fullWidth
                    helperText={`Cuota mensual base ${
                      tieneProyeccion ? "actual" : "propuesta"
                    } para esta clase`}
                  />
                </Grid>
                <Grid size={4}>
                  <SuiTypography variant="body2" fontWeight="bold">
                    Tasa de Interés Futuro (%)
                  </SuiTypography>
                  <TasaInteresField
                    clase={clase}
                    valor={configClases[clase]?.tasaBase ?? 0}
                    onChange={handleConfigChange}
                  />
                </Grid>
                <Grid size={4}>
                  <SuiTypography variant="body2" fontWeight="bold">
                    Cant. de Cuotas
                  </SuiTypography>
                  <TextField
                    value={obtenerNumeroCuotas(clase) || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Si está vacío o es un número válido
                      if (newValue === "" || /^\d+$/.test(newValue)) {
                        // Si el valor es "0" o vacío, establecemos como 0
                        const parsedValue =
                          newValue === "" ? 0 : parseInt(newValue.replace(/^0+/, ""), 10);
                        handleNumeroCuotasChange(clase, parsedValue);
                      }
                    }}
                    fullWidth
                    error={obtenerNumeroCuotas(clase) < 1}
                    helperText={
                      obtenerNumeroCuotas(clase) < 1
                        ? "El número de cuotas debe ser al menos 1"
                        : proyeccionData?.proyeccion?.[clase]
                        ? `Cuotas actuales de ${clase}: ${
                            proyeccionData.proyeccion[clase].proyeccion.length
                          } ${
                            proyeccionData.proyeccion[clase].fecha_final
                              ? `(hasta ${proyeccionData.proyeccion[clase].fecha_final})`
                              : ""
                          }`
                        : `No hay proyección para ${clase}. Los cambios se aplicarán al actualizar.`
                    }
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: { textAlign: "left" },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={6}>
                  <SuiTypography variant="body2" fontWeight="bold">
                    Pagar simultáneamente con otra clase
                  </SuiTypography>
                  <FormControl fullWidth>
                    <Select
                      value=""
                      onChange={(e) => {
                        const claseSeleccionada = e.target.value;
                        if (claseSeleccionada) {
                          handleVincularClase(clase, claseSeleccionada);
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Seleccionar clase</em>
                      </MenuItem>
                      {clases
                        .filter((c) => {
                          if (c === clase) return false;
                          // Verificar vinculaciones existentes
                          const vinculacionesActuales = proyeccionData?.clases_vinculadas || {};
                          const estaVinculada = Object.entries(vinculacionesActuales).some(
                            ([claseBase, vinculadas]) => {
                              // Verificar si la clase actual está vinculada con la clase que estamos evaluando
                              if (claseBase === clase && vinculadas.includes(c)) return true;
                              // Verificar si la clase que estamos evaluando está vinculada con la clase actual
                              if (claseBase === c && vinculadas.includes(clase)) return true;
                              return false;
                            }
                          );
                          if (estaVinculada) return false;
                          // Verificar configuración básica
                          const config = configClases[c];
                          return !!(config?.cuotaBase || config?.cuota_base);
                        })
                        .map((c) => (
                          <MenuItem key={c} value={c}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LinkIcon sx={{ fontSize: 20 }} />
                              <span>{c}</span>
                              <span>
                                (
                                {
                                  formatCurrency(
                                    configClases[c]?.cuotaBase || configClases[c]?.cuota_base || 0
                                  ).formatted
                                }
                                )
                              </span>
                              {configClases[c]?.modo_calculo === "POR_CUOTAS" && (
                                <span style={{ fontSize: "0.8em", color: "text.secondary" }}>
                                  ({configClases[c]?.numero_cuotas} cuotas)
                                </span>
                              )}
                            </Stack>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={6}>
                  <SuiTypography variant="subtitle2" fontWeight="bold">
                    Pagos simultáneos con:
                  </SuiTypography>
                  {/* Si hay clases vinculadas, mostrar los chips */}
                  {proyeccionData?.clases_vinculadas?.[clase]?.length > 0 && (
                    <Grid item xs={12}>
                      <Box>
                        <Stack direction="row" spacing={1} mt={1}>
                          {proyeccionData.clases_vinculadas[clase].map((claseVinculada) => {
                            const totalesVinculada = calcularTotalesPorClase(claseVinculada);
                            return (
                              <ClaseVinculadaChip
                                key={claseVinculada}
                                claseVinculada={claseVinculada}
                                totalDeuda={totalesVinculada.totalActivo || 0}
                                onDesvincular={() => handleDesvincular(clase, claseVinculada)}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {tieneProyeccion && (
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={12}>
                    <SuiTypography variant="body2" fontWeight="bold">
                      Proyección Pago
                    </SuiTypography>
                    <Suspense fallback={<div>Cargando proyección...</div>}>
                      <ProyeccionPreview
                        clase={clase}
                        config={{
                          ...configClases[clase],
                          totalDeuda: Number(totales.totalActivo || 0), // ✅ USAR TOTAL ACTIVO CALCULADO CON CAMPO CORRECTO
                          cuotaBase: Number(
                            configClases[clase]?.cuota_base || configClases[clase]?.cuotaBase || 0
                          ),
                          tasaBase: Number(
                            configClases[clase]?.tasa_base || configClases[clase]?.tasaBase || 0
                          ),
                          modificaciones: configClases[clase]?.modificaciones || {},
                          modo_calculo:
                            proyeccionData?.configuracion?.[clase]?.modo_calculo || "CUOTA_FIJA",
                        }}
                        fechaInicio={fechaInicio}
                        onModificarCuota={handleModificarCuotaTab}
                        numeracionInicial={obtenerNumeracionInicial(clase)}
                        claseAnteriorUltimaFecha={obtenerUltimaFechaClaseAnterior(clase)}
                        clases={clases}
                        propuestaId={propuestaId}
                        tieneProyeccion={tieneProyeccion}
                        setConfigClases={setConfigClases}
                        isFirstClass={clases.indexOf(clase) === 0}
                        isCuotaFija={configClases[clase]?.modo_calculo !== "POR_CUOTAS"}
                      />
                    </Suspense>
                  </Grid>
                </Grid>
              )}
            </Box>
          );
        })}
      </Grid>
    </Grid>
  );
};

ProyeccionTabs.propTypes = {
  activeTab: PropTypes.number.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  deudasPorClase: PropTypes.object.isRequired,
  configClases: PropTypes.object.isRequired,
  handleConfigChange: PropTypes.func.isRequired,
  fechaInicio: PropTypes.object.isRequired,
  tieneProyeccion: PropTypes.bool.isRequired,
  setTieneProyeccion: PropTypes.func,
  propuestaId: PropTypes.number,
  proceso: PropTypes.number.isRequired,
  setConfigClases: PropTypes.func.isRequired,
  proyeccionData: PropTypes.object.isRequired,
  setProyeccionData: PropTypes.func.isRequired,
  disponible: PropTypes.number.isRequired,
  onCambiosPendientes: PropTypes.func,
  tipo_propuesta: PropTypes.string,
};

export default memo(ProyeccionTabs);
